import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { RefreshCw } from 'lucide-react'
import {
  usePrescreenAllowanceQuery,
  useStartupDocumentsQuery,
  useTriggerPrescreenMutation,
} from '../../../hooks/api'

interface RunFeedbackButtonProps {
  startupId: string
}

interface TriggerError {
  error?: { message?: string }
  data?: { nextAvailableAt?: string | null }
}

function formatNextAvailable(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Hero action shown while the feedback is stale (the deck changed after the last
 * run). The only founder trigger for a fresh evaluation; each run also refreshes
 * matched investors, so it is metered: a fixed number of runs per rolling week,
 * one at a time, failed runs refunded (enforced server-side).
 */
export function RunFeedbackButton({ startupId }: RunFeedbackButtonProps) {
  const { data: allowance } = usePrescreenAllowanceQuery(startupId)
  const { data: documents } = useStartupDocumentsQuery(startupId)
  const trigger = useTriggerPrescreenMutation(startupId)

  const deck = documents?.find((doc) => doc.type === 'PITCH_DECK')
  const isRunning = allowance?.isRunning ?? false
  const outOfRuns = allowance?.runsLeft === 0
  const triggerError = trigger.error as TriggerError | null

  let status: string | null = null
  if (isRunning) {
    status = 'Running, about 5 minutes'
  } else if (outOfRuns && allowance?.nextAvailableAt) {
    status = `Next run available ${formatNextAvailable(allowance.nextAvailableAt)}`
  } else if (allowance) {
    status = `${allowance.runsLeft} of ${allowance.runsAllowed} runs left this week`
  }

  const errorMessage = triggerError?.data?.nextAvailableAt
    ? `No runs left. Next run available ${formatNextAvailable(triggerError.data.nextAvailableAt)}`
    : (triggerError?.error?.message ?? (triggerError ? 'Could not start the run' : null))

  return (
    <div className="flex items-center gap-3">
      {errorMessage && !trigger.isPending ? (
        <span className="text-xs text-destructive">{errorMessage}</span>
      ) : (
        status && <span className="text-xs text-muted-foreground">{status}</span>
      )}
      <CtaButton
        type="button"
        size="sm"
        icon={<RefreshCw />}
        title="Scores your current deck and refreshes your matched investors"
        loading={trigger.isPending}
        disabled={!deck || !allowance || isRunning || outOfRuns}
        onClick={() => {
          if (!deck) return
          trigger.reset()
          trigger.mutate({ path: { startupId, documentId: deck.documentId }, body: {} })
        }}
      >
        Refresh feedback
      </CtaButton>
    </div>
  )
}
